import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'react-feather'
import Button from './button'
import { Link } from 'react-router-dom'
import { useGetMedia } from 'hooks'
import { Modal } from 'components'

const getItemsPerPage = () => {
  if (typeof window === 'undefined') return 3
  const width = window.innerWidth
  if (width < 600) return 1
  if (width < 960) return 2
  return 3
}

const AdvancedCarousel = () => {
  const { media } = useGetMedia()
  const [currentPage, setCurrentPage] = useState(0)
  const [selectedItem, setSelectedItem] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(getItemsPerPage)

  const hasSlides = Array.isArray(media) && media.length > 0
  const totalItems = hasSlides ? media.length : 0
  const pageCount = hasSlides ? Math.ceil(totalItems / itemsPerPage) : 0

  useEffect(() => {
    const handleResize = () => {
      setItemsPerPage(getItemsPerPage())
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!hasSlides || pageCount <= 1) return

    const interval = setInterval(() => {
      setCurrentPage((prev) => (prev + 1) % pageCount)
    }, 6000)

    return () => clearInterval(interval)
  }, [hasSlides, pageCount])

  useEffect(() => {
    if (pageCount === 0) {
      setCurrentPage(0)
    } else if (currentPage >= pageCount) {
      setCurrentPage(0)
    }
  }, [pageCount, currentPage])

  const handleNext = () => {
    if (!hasSlides || pageCount <= 1) return
    setCurrentPage((prev) => (prev + 1) % pageCount)
  }

  const handlePrev = () => {
    if (!hasSlides || pageCount <= 1) return
    setCurrentPage((prev) => (prev - 1 + pageCount) % pageCount)
  }

  const handleDotClick = (index) => {
    if (!hasSlides) return
    setCurrentPage(index)
  }

  const openModal = (item) => {
    setSelectedItem(item)
  }

  const closeModal = () => {
    setSelectedItem(null)
  }

  if (!hasSlides) {
    return (
      <div className='w-full flex flex-col items-center justify-center py-8'>
        <p className='text-sm text-[#6C757D]'>No gallery media available yet.</p>
      </div>
    )
  }

  const startIndex = currentPage * itemsPerPage
  const pageItems = media.slice(startIndex, startIndex + itemsPerPage)

  return (
    <div className='relative w-full max-w-6xl mx-auto flex flex-col items-center'>
      <div className='relative w-full overflow-hidden rounded-2xl border border-solid border-[#eee] bg-white shadow-sm px-4 sm:px-6 py-6'>
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'>
          {pageItems.map((item) => (
            <button key={item?.id} type='button' onClick={() => openModal(item)} className='group flex flex-col text-left focus:outline-none'>
              <div className='w-full h-[180px] sm:h-[220px] md:h-[240px] rounded-xl overflow-hidden'>
                <img
                  src={item?.media_link}
                  alt={item?.title}
                  className='w-full h-full object-cover group-hover:scale-105 transition-transform duration-300'
                />
              </div>
              {item?.title && (
                <p className='mt-3 text-center text-xs sm:text-sm md:text-base text-[#495057] font-medium line-clamp-2'>{item.title}</p>
              )}
            </button>
          ))}
        </div>

        {/* Navigation */}
        {pageCount > 1 && (
          <>
            <button
              aria-label='Previous slide'
              className='absolute left-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/90 hover:bg-white shadow rounded-full flex items-center justify-center transition-colors'
              onClick={handlePrev}
            >
              <ChevronLeft className='h-4 w-4 sm:h-5 sm:w-5 text-[#495057]' />
            </button>

            <button
              aria-label='Next slide'
              className='absolute right-4 top-1/2 -translate-y-1/2 p-2 sm:p-3 bg-white/90 hover:bg-white shadow rounded-full flex items-center justify-center transition-colors'
              onClick={handleNext}
            >
              <ChevronRight className='h-4 w-4 sm:h-5 sm:w-5 text-[#495057]' />
            </button>
          </>
        )}
      </div>

      {/* Pagination Dots */}
      {pageCount > 1 && (
        <div className='mt-4 flex justify-center space-x-2'>
          {Array.from({ length: pageCount }).map((_, index) => (
            <button
              key={index}
              aria-label={`Go to slide ${index + 1}`}
              className={`w-2 h-2 rounded-full transition-colors ${currentPage === index ? 'bg-[#BF5540]' : 'bg-gray-300'}`}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>
      )}

      <Link to='/gallery'>
        <Button className='mt-8 sm:mt-10'>View Gallery</Button>
      </Link>

      {selectedItem && (
        <Modal isOpen={!!selectedItem} close={closeModal} dialogClassName='!max-w-4xl !w-full'>
          <div className='w-full h-full flex flex-col'>
            <div className='flex justify-end mb-3'>
              <button type='button' onClick={closeModal} className='text-sm text-[#495057] hover:text-[#BF5540]'>
                Close
              </button>
            </div>
            <div className='w-full max-h-[70vh] rounded-xl overflow-hidden border border-solid border-[#eee]'>
              <img src={selectedItem.media_link} alt={selectedItem.title} className='w-full h-full object-contain bg-black/5' />
            </div>
            {selectedItem.title && <p className='mt-4 text-center text-sm sm:text-base text-[#495057] font-medium'>{selectedItem.title}</p>}
          </div>
        </Modal>
      )}
    </div>
  )
}

export default AdvancedCarousel
